import lodash from 'lodash';
import tallyAndValidateChoiceSets from './tallyAndValidateChoiceSets';
import choiceSetsWithQuantities from './choiceSetsWithQuantities';
import tallyAndValidateNestedChoicesets from './tallyAndValidateNestedChoicesets';
import nestedChoiceSetsWithQuantities from './nestedChoiceSetsWithQuantities';
import adjustChoiceSetsForQuantityDiscount from './adjustChoiceSetsForQuantityDiscount';
import adjustChoiceSetsForQuantityDiscountV2 from './adjustChoiceSetsForQuantityDiscountV2';
import adjustNestedChoiceSetsForQuantityDiscountV2 from './adjustNestedChoiceSetsForQuantityDiscountV2';
import enhanceItem from './enhanceItem';
import sortChoices from './sortChoices';
const NOT_SWAP_REGEX = /^((?!(swap)).)*$/;

export default function enhancePurchase(
  purchase: _Purchase,
  menu: Menu,
  choiceOrderingMethod: ChoiceOrderingMethod,
  choiceSetDiscountConfig?: ChoiceSetQuantityDiscountConfig,
  previouslyStaged?: boolean | undefined,
): Purchase | undefined {
  if (purchase.unavailable) return;

  const rawItem = menu.items[purchase.itemId];
  if (!rawItem) return;
  const rootItem = enhanceItem(rawItem, menu);

  if (!rootItem) return;
  const isComposite = !!rootItem.sizes.length;

  const item =
    (isComposite && rootItem.sizes.find(s => s.id === purchase.sizeId)) ||
    rootItem;

  // BOH required fields for items (needed for coupons)
  const _plucode = item.plucode ?? rootItem.plucode;
  const _class = item.class ?? rootItem.class;
  const _category = item.category ?? rootItem.category;
  // if (!_plucode || !_class || !_category) return;

  const isNested = !!item.choiceSets.find(set => {
    // console.log('Set===>', set);
    return (
      ('nestedIngredients' in set &&
        set.nestedIngredients &&
        // checkbox is used to implement the "make it a meal" buttons found on olo2,
        set.displayType !== 'checkbox') ||
      set.displayType === 'makeameal'
    );
  });
  let choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] = [];
  const miam = purchase.miam;
  if (isNested) {
    choiceSets = tallyAndValidateNestedChoicesets(
      nestedChoiceSetsWithQuantities(
        item.choiceSets as NestedChoiceSet[],
        purchase.choiceSelections as NestedChoiceSelections,
        choiceOrderingMethod,
        choiceSetDiscountConfig,
        previouslyStaged,
        undefined,
        miam,
      ),
    );
  } else {
    choiceSets = tallyAndValidateChoiceSets(
      choiceSetsWithQuantities(
        item.choiceSets,
        purchase.choiceSelections as ChoiceSelections,
      ),
      choiceSetDiscountConfig,
    );
  }
  // filter out conditional choicesets where condition has not been met
  let filterList: string[] = [];

  lodash.keys(item.conditionalChoiceSets).forEach(conditionChoiceId => {
    const found = Boolean(
      choiceSets.find(choiceSet => {
        return Boolean(
          choiceSet.choices.find(
            choice => choice.id === conditionChoiceId && choice.quantity,
          ),
        );
      }),
    );

    if (!found) {
      filterList = lodash.uniq([
        ...filterList,
        ...item.conditionalChoiceSets[conditionChoiceId],
      ]);
    }
  });

  choiceSets = choiceSets.filter(
    choiceSet => !filterList.includes(choiceSet.key),
  );

  if (choiceSetDiscountConfig) {
    const {
      useChoiceSetDiscountMap = true,
      choiceSetKey,
      // choiceSetDiscountMap = {},
      discountPlu,
    } = choiceSetDiscountConfig;

    if (!useChoiceSetDiscountMap && choiceSetKey && discountPlu) {
      choiceSets = adjustChoiceSetsForQuantityDiscount(
        choiceSets,
        choiceSetDiscountConfig,
      );
    } else if (useChoiceSetDiscountMap) {
      if (isNested) {
        choiceSets = adjustNestedChoiceSetsForQuantityDiscountV2(
          choiceSets,
          choiceSetDiscountConfig,
          purchase.choiceSelections,
        );
      } else {
        choiceSets = adjustChoiceSetsForQuantityDiscountV2(
          choiceSets,
          choiceSetDiscountConfig,
        );
      }
    }
  }

  let choicesWithQuantity: ChoiceWithQuantity[] = [];
  lodash
    .values(choiceSets)
    .filter(choiceSet => NOT_SWAP_REGEX.test(choiceSet.key)) //fiter out Boost swap choiceset
    .forEach(set => {
      choicesWithQuantity = [
        ...choicesWithQuantity,
        ...createChociesWithQuantity(set, choiceOrderingMethod),
      ];
    });

  const valid = Object.values(choiceSets).reduce(
    (acc, choiceSet) => acc && Boolean(choiceSet.valid),
    true,
  );

  const popularChoices: PopularChoice[] = [];

  item.popularChoices.forEach(choiceId => {
    choiceSets.forEach(choiceSet => {
      choiceSet.choices.forEach(choice => {
        if (choice.id === choiceId) {
          popularChoices.push({ choice, choiceSetKey: choiceSet.key });
        }
      });
    });
  });
  return {
    mimCategoryId: purchase.mimCategoryId,
    id: purchase.id,
    item: rootItem,
    choiceSets,
    substitutionSets: item.substitutionSets,
    valid,
    quantity: purchase.quantity,
    choicesWithQuantity,
    baseMoneyPrice: item.baseMoneyPrice,
    basePointsPrice: item.basePointsPrice,
    plucode: _plucode || '',
    class: _class || '',
    category: _category || '',
    popularChoices,
    brandId: purchase.brandId,
    sizeId: purchase.sizeId,
    choiceSelections: purchase.choiceSelections,
    substitutionSelections: purchase.substitutionSelections,
    miamQuantityMap: purchase.miamQuantityMap,
    miam: purchase.miam,
  };
}

const createChociesWithQuantity = (
  choiceSet:
    | ValidatedChoiceSet
    | ValidatedNestedChoiceSet
    | ChoiceSetWithQuantity,
  choiceOrderingMethod: ChoiceOrderingMethod,
): (ChoiceWithQuantity | NestedChoiceWithQuantity)[] => {
  const result: (ChoiceWithQuantity | NestedChoiceWithQuantity)[] = [];

  let freeChoicesRemaining = choiceSet.free;

  const sortedChoices = sortChoices(choiceSet.choices, choiceOrderingMethod);

  sortedChoices.forEach(choice => {
    let remainingQuantity = choice.quantity;
    let freeQuantity = 0;

    if (freeChoicesRemaining && choice.quantity) {
      freeQuantity = Math.min(freeChoicesRemaining, choice.quantity);
      freeChoicesRemaining -= freeQuantity;
      remainingQuantity = choice.quantity - freeQuantity;
    }
    const _choices: (ChoiceWithQuantity | NestedChoiceWithQuantity)[] = [];
    if (freeQuantity) {
      _choices.push({
        ...choice,
        quantity: freeQuantity,
        moneyPrice: 0,
        basePointsPrice: 0,
        freeChoice: Boolean(choiceSet.free),
      });
    }
    if (remainingQuantity) {
      _choices.push({
        ...choice,
        quantity: remainingQuantity,
        moneyPrice: choice.baseMoneyPrice * remainingQuantity,
        freeChoice: Boolean(choiceSet.free),
      });
    }

    // disgusting typing patch fix here
    if (
      'choiceSets' in choice &&
      (choice as NestedChoiceWithQuantity).choiceSets &&
      _choices.length === 1
    ) {
      let nestedChoicesets: (
        | ChoiceSetWithQuantity
        | NestedChoiceSetWithQuantity
      )[] = [];
      (choice as NestedChoiceWithQuantity).choiceSets.forEach(set => {
        nestedChoicesets = [
          ...nestedChoicesets,
          {
            ...set,
            choices: [...createChociesWithQuantity(set, choiceOrderingMethod)],
          },
        ];
      });
      (_choices[0] as NestedChoiceWithQuantity).choiceSets = nestedChoicesets;
    }

    result.push(..._choices);
  });

  return result;
};
